import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Injector,
  Input,
  Output,
  Renderer2,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IButtonConfig } from 'shared/components/atoms/button/button.component';
import { PopupFooterComponent, PopupHeaderComponent } from 'shared/components/molecules';
import { PopupContentBaseComponent } from 'shared/components/organisms/popup/popup-content-base.component';

@Component({
    selector: 'app-popup-host',
    templateUrl: './popup-host.component.html',
    imports: [PopupHeaderComponent, PopupFooterComponent, NgIf, NgClass],
    styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;

      ::ng-deep .popup-content {
        overflow-y: auto;
      }
    }
  `
})
export class PopupHostComponent implements AfterViewInit {
  public readonly dialogRef = inject(MatDialogRef<PopupHostComponent>);
  public readonly renderer = inject(Renderer2);
  public readonly elementRef = inject(ElementRef);
  @Input() public component!: Type<PopupContentBaseComponent<unknown>>;
  @Output() public closeEvent: EventEmitter<never> = new EventEmitter<never>();
  @ViewChild('dialogContent', { static: false, read: ViewContainerRef })
  public container!: ViewContainerRef;
  public componentRef!: ComponentRef<PopupContentBaseComponent<unknown>>;
  public footerClass: FooterClass = '';
  public showFooter = true;
  private injector!: Injector;

  constructor(@Inject(MAT_DIALOG_DATA) public data: IPopupConfig) {}

  public ngAfterViewInit(): void {
    this.footerClass = this.data.footerClass ?? this.footerClass;
    this.injector = Injector.create({
      providers: [
        {
          provide: 'data',
          useValue: this.data,
        },
        {
          provide: MatDialogRef,
          useValue: this.dialogRef,
        },
      ],
    });

    if (this.data.component) {
      this.placeComponent(this.data.component, this.data.componentInputs);
    }
  }

  public placeComponent(
    component: Type<PopupContentBaseComponent<unknown>>,
    componentInputs?: Record<string, unknown>
  ): void {
    this.container.clear();
    this.componentRef = this.container.createComponent(component, {
      injector: this.injector,
    });
    if (componentInputs) {
      Object.entries(componentInputs).forEach(([key, value]) => {
        this.componentRef.setInput(key, value);
      });
    }

    // Wait for child component to initialize
    setTimeout(() => {
      // add class to popup content element
      const componentHostElement = this.componentRef.location.nativeElement;
      this.renderer.addClass(componentHostElement, 'popup-content');
      if (this.data.fillHeight) {
        this.renderer.addClass(this.elementRef.nativeElement, 'popup-fill');
      }

      // move custom footer to this component's DOM to make styling/responsive easier
      if (this.componentRef.instance.customFooter) {
        const footerElement = componentHostElement.querySelector('app-popup-footer');
        this.renderer.appendChild(this.elementRef.nativeElement, footerElement);
        this.showFooter = false;
      }
    });
  }

  public onClose(): void {
    this.dialogRef.close();
  }
}

export type FooterClass = 'buttons-right' | 'buttons-left' | 'buttons-center' | '';

export interface IPopupConfig {
  component: Type<PopupContentBaseComponent<unknown>>;
  componentInputs?: Record<string, unknown>;
  title: string;
  fillHeight?: boolean;
  hideHeaderBorder?: boolean;
  footerClass?: FooterClass;
  // TODO: implement this properly post MVP
  contentData?: number | string | unknown;
  subTitle?: string;
  buttons?: IButtonConfig[];
  layoutConfig?: { showHeaderBorder?: boolean };
  isPrimary?: boolean;
  showCustomHeader?: boolean;
  width?: string;
}
